/**
 * PURE. «Наугад» (creatures §A7.1) and deterministic ghost / NPC appearance (§A12.5):
 * `appearanceFromRng(rng, opts)` consumes the caller's stream; `ghostDNA(seed)` = `appearanceFromRng(new Rng(seed + ':look'))`.
 */
import { Rng, type CreatureDNA } from '../../shared';
import { FUR_PALETTE_IDS, PARTS, TINCTURE_IDS, isMetalTincture, type PartOption } from '../../content/creature/parts';
import { CURATED_COATS, GHOST_SPECIES_WEIGHTS, SPECIES, speciesAt, type SpeciesDef } from '../../content/creature/species';
import { buildDNA, withResolvedName } from './codec';
import { DNA_FIELDS, fieldMax, type FieldDef } from './fields';
import { namePoolFor } from './names';
import { fieldsOf, speciesFields, type FieldMap } from './presets';

export interface RandomOpts {
  /** Derp dial `d ∈ [0,1]` («Благообразно ↔ Нелепо», default 0.35). */
  derp?: number;
  /** Field names that must not change (pins, §A7.2). */
  locks?: ReadonlySet<string>;
  /** `<slot>.<id>` keys the player owns; undefined = everything owned (ghosts). */
  owned?: ReadonlySet<string>;
  /** Only these §A12.1 groups are re-rolled («Этот раздел»). */
  groups?: ReadonlySet<string>;
}

/** Options that only make sense on their own species' anatomy (weight 0 elsewhere). */
const EXCLUSIVE = new Set(['ears.stalks', 'feet.snail', 'arms.wings', 'snout.none', 'hands.talons', 'feet.bird', 'legs.stilts', 'belly.ruff', 'tail.feather']);
/** Fields never rolled by «Наугад» (identity is rolled separately; species stay unless the caller rolls them). */
const SKIP = new Set(['ver', 'bodySpecies', 'headSpecies', 'look', 'crc', 'soulSeed', 'nameMode', 'pronoun', 'title', 'name', 'epithet', 'humour', 'voice',
  'furBase', 'furStripe', 'furBelly', 'hairColor', 'tinctA', 'tinctB', 'ordTinct', 'chargeTinct', 'ruleBreak', 'hetero', 'iris2', 'catchlight']);

const tagFactor = (o: PartOption, d: number): number =>
  (o.tags.includes('d') ? 0.2 + 3 * d : 1) * (o.tags.includes('n') ? 1.6 - d : 1);

/** Signature / native option sets of a species (defaults + every look). */
function nativeSets(s: SpeciesDef): { signature: Set<string>; native: Set<string> } {
  const signature = new Set<string>();
  const native = new Set<string>();
  const add = (set: Set<string>, preset: Readonly<Record<string, string | number>>): void => {
    for (const [k, v] of Object.entries(preset)) if (typeof v === 'string') set.add(`${k}=${v}`);
  };
  add(signature, s.defaults);
  for (const l of s.looks) add(native, l.preset);
  add(native, s.defaults);
  return { signature, native };
}

/** Enum weight per creatures §A7.1. */
function enumWeight(f: FieldDef, o: PartOption, s: SpeciesDef, sets: ReturnType<typeof nativeSets>, d: number, owned?: ReadonlySet<string>): number {
  const slotKey = `${f.slot}.${o.id}`;
  if (owned && !owned.has(slotKey) && o.acquisition !== 'default') return 0;
  let base: number;
  if (sets.signature.has(`${f.name}=${o.id}`)) base = 4;
  else if (sets.native.has(`${f.name}=${o.id}`) || o.native.includes(s.id)) base = 1;
  else if (o.native.length > 0) base = EXCLUSIVE.has(slotKey) ? 0 : 0.25;
  else if (o.tags.includes('g')) base = 0.25;
  else if (o.tags.includes('u')) base = 0.3;
  else base = 0.6;
  return base * tagFactor(o, d);
}

/** Box–Muller normal from the stream. */
function normal(rng: Rng): number {
  const u = Math.max(1e-9, rng.float());
  const v = rng.float();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/** The species owning a field (head fields → head species). */
const HEAD_GROUPS = new Set(['head', 'eyes', 'face']);

/** Re-rolls the fields of `m` in DNA field order (creatures §A7.1); deterministic in (rng state, d, locks, species, owned). */
export function rollFields(m: FieldMap, rng: Rng, opts: RandomOpts = {}): FieldMap {
  const d = Math.max(0, Math.min(1, opts.derp ?? 0.35));
  const body = speciesAt(m.bodySpecies);
  const head = speciesAt(m.headSpecies);
  const ref = speciesFields(body, { head, look: m.look });
  const setsB = nativeSets(body);
  const setsH = nativeSets(head);
  for (const f of DNA_FIELDS) {
    if (SKIP.has(f.name) || opts.locks?.has(f.name)) continue;
    if (opts.groups && !opts.groups.has(f.group)) continue;
    const isHead = HEAD_GROUPS.has(f.group) && f.name !== 'headwear';
    const s = isHead ? head : body;
    if (f.kind === 'slider') {
      const top = fieldMax(f);
      let q: number;
      if (rng.chance(0.15 * d)) q = rng.chance(0.5) ? 0 : top;
      else q = Math.round(ref[f.name] + normal(rng) * (0.12 + 0.5 * d) * top);
      m[f.name] = Math.max(0, Math.min(top, q));
    } else if (f.kind === 'enum' && f.slot && f.slot in PARTS) {
      const list = (PARTS as Record<string, readonly PartOption[]>)[f.slot];
      const sets = isHead ? setsH : setsB;
      const ws = list.map((o) => ({ w: enumWeight(f, o, s, sets, d, opts.owned), v: o.index }));
      if (ws.some((x) => x.w > 0)) m[f.name] = rng.weighted(ws);
    } else if (f.kind === 'dye') {
      m[f.name] = rng.int(0, 9);
    }
  }
  // Colours: with probability 1 − 0.6d a curated triple, else random palettes.
  if (!opts.locks?.has('furBase') && (!opts.groups || opts.groups.has('coat'))) {
    const curated = CURATED_COATS[body.id] ?? CURATED_COATS.cat;
    const idx = (id?: string): number => (id ? (FUR_PALETTE_IDS as readonly string[]).indexOf(id) : 31);
    if (rng.chance(1 - 0.6 * d)) {
      const [b, st, be] = rng.pick(curated);
      m.furBase = idx(b); m.furStripe = idx(st); m.furBelly = idx(be);
    } else {
      m.furBase = rng.int(0, 13);
      m.furStripe = rng.chance(0.6) ? 31 : rng.int(0, 13);
      m.furBelly = rng.chance(0.7) ? 31 : rng.int(0, 13);
    }
    m.hairColor = rng.chance(0.7) ? 31 : rng.int(0, 13);
  }
  if (!opts.locks?.has('iris') && (!opts.groups || opts.groups.has('eyes'))) {
    m.hetero = rng.chance(0.04 + 0.1 * d) ? 1 : 0;
    m.iris2 = rng.int(0, 6);
    m.catchlight = rng.chance(0.15) ? 1 : 0;
  }
  return fixCompat(m);
}

/** DNA-level §A8 fixes (the renderer-side rules live in `resolveSpec`). */
export function fixCompat(m: FieldMap): FieldMap {
  const body = speciesAt(m.bodySpecies);
  // Wings allow cape 0/1 only.
  if (m.wings > 0 && m.cape > 1) m.wings = 0;
  // Snail body: no footwear, snail foot.
  if (body.rig.slugBody) { m.footwear = 0; m.feet = PARTS.feet.findIndex((o) => o.id === 'snail'); }
  // Wing-arm species keep wing arms; non-wing species never get them.
  const wingIdx = PARTS.arms.findIndex((o) => o.id === 'wings');
  if (body.rig.wingArms) m.arms = wingIdx; else if (m.arms === wingIdx) m.arms = 0;
  // Eye-stalks only on a snail head; a snail head always has them.
  const stalks = PARTS.ears.findIndex((o) => o.id === 'stalks');
  const head = speciesAt(m.headSpecies);
  if (head.rig.eyeStalks) m.ears = stalks; else if (m.ears === stalks) m.ears = 0;
  return m;
}

/** Heraldry with the rule of tincture (creatures §A11.2; ruleBreak 5 %). */
function rollHeraldry(m: FieldMap, rng: Rng): void {
  m.shield = rng.int(0, 5); m.division = rng.int(0, 11); m.ordinary = rng.int(0, 9);
  m.charge = rng.int(0, 7); m.chargeCount = rng.int(0, 2); m.motto = rng.int(0, 15);
  m.ruleBreak = rng.chance(0.05) ? 1 : 0;
  const metal = (): number => rng.int(0, 1);
  const colour = (): number => rng.int(2, 6);
  m.tinctA = rng.chance(0.5) ? metal() : colour();
  const opposite = (t: number): number => (m.ruleBreak ? rng.int(0, TINCTURE_IDS.length - 2) : isMetalTincture(t) ? colour() : metal());
  m.tinctB = opposite(m.tinctA);
  m.ordTinct = opposite(m.tinctA);
  m.chargeTinct = opposite(m.ordinary ? m.ordTinct : m.tinctA);
}

export interface AppearanceOpts {
  /** Force species (otherwise the §A12.5 weights). */
  bodySpecies?: string;
  headSpecies?: string;
  /** Норов index (ghost personality mapping, §A10). */
  humour?: number;
  derp?: number;
}

/** creatures §A12.5: a full appearance from the caller's stream. Returns the `M…` code via the returned DNA. */
export function appearanceFromRng(rng: Rng, opts: AppearanceOpts = {}): CreatureDNA {
  const d = opts.derp ?? 0.2 + 0.6 * rng.float();
  const bodyIdx = opts.bodySpecies ? SPECIES.findIndex((s) => s.id === opts.bodySpecies) : rng.weighted(GHOST_SPECIES_WEIGHTS.map((w, i) => ({ w, v: i })));
  const body = speciesAt(bodyIdx);
  let head = body;
  if (opts.headSpecies) head = SPECIES.find((s) => s.id === opts.headSpecies) ?? body;
  else if (rng.chance(0.12)) head = speciesAt(rng.weighted(GHOST_SPECIES_WEIGHTS.map((w, i) => ({ w, v: i }))));
  const look = rng.int(0, body.looks.length - 1);
  const m = speciesFields(body, { head, look });
  rollFields(m, rng, { derp: d });
  m.pronoun = rng.chance(0.4) ? 1 : 0;
  const [lo, hi] = namePoolFor(m.pronoun as 0 | 1);
  m.title = rng.int(0, 31); m.name = rng.int(lo, hi); m.epithet = rng.int(0, 47);
  m.humour = opts.humour ?? rng.int(0, 5);
  m.voice = rng.int(0, 5);
  m.voicePitch = rng.int(1, 6);
  rollHeraldry(m, rng);
  m.soulSeed = Math.floor(rng.float() * 65536);
  return buildDNA(m);
}

/** `ghostDNA(seedString)` (creatures §A12.5, combat §4.8): a pure function of `(seed, contentVersion)`. */
export function ghostDNA(seed: string, opts: AppearanceOpts = {}): CreatureDNA {
  return appearanceFromRng(new Rng(`${seed}:look`), opts);
}

/** «Наугад» for the constructor: re-rolls unpinned fields of an existing DNA (species and identity stay). */
export function randomizeDNA(dna: CreatureDNA, seed: string, opts: RandomOpts = {}): CreatureDNA {
  const m = fieldsOf(dna);
  rollFields(m, new Rng(`${seed}:naugad`), opts);
  return withResolvedName(buildDNA(m, dna.fields.nameMode ? dna.name : null));
}
